import React, { useEffect, useState } from 'react';
import { Flex, Text, Grid, Icon, RadioGroup, Tag } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useUpdateSinglePlanMutation,
  useGetCoverageComparisonQuery,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import { toast } from 'react-toastify';
import { extractErrorMsg } from 'utils/errors';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import {
  coverageTypeEnum,
  studentStatusEnum,
  backgroundTypeEnum,
  coverageTypes,
  planPaymentMode,
  planStatusEnum,
} from 'common/utils/enums';
import {
  saveCoverageType,
  selectCoverageType,
  saveCoverageSelection,
  selectCoverageSelection,
  saveUpfrontSplitAmount,
  selectUpfrontSplitAmount,
} from 'redux/features/plans/plansSlice';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import { ApplyFormLayout } from 'common/layouts/apply-form';
import CoverageTypeSelector from '../plan-step-choose-coverage/coverage-type-selector';
import PaymentPlanCard from 'components/plans/payment-plan-card';
import { BiSolidBadgeCheck } from 'react-icons/bi';
import { InfoDetail } from 'components/shared/member-info-card';
import {
  Accordion,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from '@chakra-ui/react';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import { isWithinDateRange } from 'common/utils/dateRange';
import { getTerm } from 'common/utils/getTerm';
import { leaseTypeEnum } from 'common/utils/enums';
import moment from 'moment';
import Button from 'common/components/button';
import { calculateSavings, SavingsCalculator } from 'components/coverages/savings-calculator';
import { DEFAULT_DEDUCTIBLE } from 'common/utils/consts';
import { handleSmoothScroll } from 'common/hooks/handleSmoothScroll';
import IconImage from 'common/components/icon-image';

const PlanStepCoverageType = ({
  onBackClick,
  onNextClick,
  planData,
  stepNumber,
  onShowSavingsCalculator,
}) => {
  // HOOKS
  const { trackPageLoad, trackCustomEvent } = useUserTracker();
  const [updatePlan, { isLoading: updatePlanLoading }] =
    useUpdateSinglePlanMutation();
  const dispatch = useDispatch();
  const coverageType = useSelector(selectCoverageType);
  const coverageSelection = useSelector(selectCoverageSelection);
  const upfrontSplit = useSelector(selectUpfrontSplitAmount);
  const brand = planData?.property?.brand;
  const isDarkBg = brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;

  // Get coverage comparison data from API
  const {
    data: coverageComparisonData,
    isLoading: isLoadingComparison,
    error: comparisonError,
  } = useGetCoverageComparisonQuery(planData?.id, {
    skip: !planData?.id,
  });

  // SIDE EFFECTS
  useEffect(() => {
    trackPageLoad('PlanStepCoverageType');
  }, []);

  // Check if limited coverage is available
  const isLimitedCoverageAvailable = React.useMemo(() => {
    return coverageComparisonData?.hasLimitingDates || false;
  }, [coverageComparisonData?.hasLimitingDates]);

  // Check if leave anytime is available
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const leaveAnytime = planData?.coverage?.leaveAnytime;
  const isMonthToMonth =
    planData?.lease?.leaseType === leaseTypeEnum.MONTH_TO_MONTH_LEASE;

  const leaveAnytimeEligible = React.useMemo(() => {
    const inRange = isWithinDateRange(
      {
        startDate: now,
        endDate: leaveAnytime?.endDate,
      },
      now
    );
    const length =
      getTerm(planData?.lease?.startDate, planData?.lease?.endDate) >= 6;

    // If plan is tied to a property, check if leave anytime is enabled
    let allowed = true;
    if (planData?.property) {
      allowed = planData?.property?.leaveAnytimeEnabled;
    }

    return isMonthToMonth ? allowed : inRange && length && allowed;
  }, [
    now,
    leaveAnytime,
    isMonthToMonth,
    planData?.lease?.startDate,
    planData?.lease?.endDate,
    planData?.property,
  ]);

  // If neither limited coverage nor leave anytime is available, skip this step
  useEffect(() => {
    if (
      !isLoadingComparison &&
      !isLimitedCoverageAvailable &&
      !leaveAnytimeEligible
    ) {
      onNextClick();
    }
  }, [
    isLoadingComparison,
    isLimitedCoverageAvailable,
    leaveAnytimeEligible,
    onNextClick,
  ]);

  // Initialize coverage type from plan data if available
  useEffect(() => {
    const planCoverageType = planData?.coverage?.leaveAnytime?.coverageType;
    if (planCoverageType && planCoverageType !== coverageType) {
      dispatch(saveCoverageType(planCoverageType));
    }
  }, [planData?.coverage?.leaveAnytime?.coverageType, coverageType, dispatch]);

  // Initialize upfront split amount
  useEffect(() => {
    if (upfrontSplit === null && leaveAnytime?.amount) {
      dispatch(saveUpfrontSplitAmount(leaveAnytime.amount / 2));
    }
  }, [leaveAnytime?.amount, upfrontSplit, dispatch]);

  // Initialize coverage selection to default to upfront payment
  useEffect(() => {
    if (!coverageSelection[coverageTypes.LEAVE_ANYTIME]) {
      dispatch(
        saveCoverageSelection({
          ...coverageSelection,
          [coverageTypes.LEAVE_ANYTIME]: planPaymentMode.ONE_TIME,
        })
      );
    }
  }, [coverageSelection, dispatch]);

  // Handle coverage type change
  const handleCoverageTypeChange = async (newType) => {
    try {
      await updatePlan({
        planId: planData.id,
        status: planStatusEnum.PENDING,
        coverage: {
          leaveAnytime: {
            coverageType: newType,
          },
        },
      }).unwrap();

      dispatch(saveCoverageType(newType));

      // Track the change
      trackCustomEvent('Coverage Type Changed', {
        from: coverageType,
        to: newType,
        planId: planData.id,
      });
    } catch (error) {
      const errMsg = extractErrorMsg(error, 'Update Failed');
      toast.error(errMsg);
    }
  };

  // Format amount helper
  const formatAmount = (amount) => {
    return FormatAsCurrency(amount, planData?.lease?.currency);
  };

  // Get the appropriate coverage data based on selected type
  const getCoverageData = () => {
    if (isLimitedCoverageAvailable && coverageComparisonData) {
      return coverageType === coverageTypeEnum.LIMITED
        ? coverageComparisonData.limited
        : coverageComparisonData.full;
    }
    return leaveAnytime;
  };

  const currentCoverageData = getCoverageData();

  // Payment amounts
  const leaveAnytimeOnetimeAmount =
    currentCoverageData?.payment?.onetime?.amount;
  const leaveAnytimeSubscriptionAmount =
    currentCoverageData?.payment?.subscription?.amount;
  const leaveAnytimeSplitAmount = currentCoverageData?.amount;

  const leaveAnytimeOnetimeTotal = formatAmount(leaveAnytimeOnetimeAmount);
  const leaveAnytimeSubscriptionTotal = formatAmount(
    leaveAnytimeSubscriptionAmount
  );
  const leaveAnytimeSplitTotal = formatAmount(leaveAnytimeSplitAmount);

  const monthlySplitAmount = upfrontSplit
    ? (leaveAnytimeSplitAmount - upfrontSplit) / currentCoverageData?.term
    : 0;
  const monthlySplitTotal = formatAmount(monthlySplitAmount);

  // Calculate savings
  const [savings, setSavings] = useState({});
  useEffect(() => {
    let amount = null;
    let upfrontAmount = null;

    const leaveAnytimeSelection =
      coverageSelection[coverageTypes.LEAVE_ANYTIME];

    if (leaveAnytimeSelection === planPaymentMode.ONE_TIME) {
      upfrontAmount += leaveAnytimeOnetimeAmount;
    } else if (leaveAnytimeSelection === planPaymentMode.SPLIT) {
      amount += monthlySplitAmount;
      upfrontAmount += upfrontSplit;
    } else if (leaveAnytimeSelection === planPaymentMode.SUBSCRIPTION) {
      amount += leaveAnytimeSubscriptionAmount;
    }

    setSavings(
      calculateSavings(
        planData?.property,
        leaveAnytime?.deductible || DEFAULT_DEDUCTIBLE,
        planData?.lease,
        amount,
        upfrontAmount
      )
    );
  }, [
    coverageSelection,
    leaveAnytimeOnetimeAmount,
    leaveAnytimeSubscriptionAmount,
    upfrontSplit,
    monthlySplitAmount,
    leaveAnytime?.deductible,
    planData?.property,
    planData?.lease,
  ]);

  const leaveAnytimeSubscriptionStartDateText = React.useMemo(() => {
    if (!leaveAnytime?.startDate) return 'today';
    const startDate = moment(leaveAnytime.startDate);
    const today = moment(now);

    if (startDate.isSameOrBefore(today, 'day')) {
      return 'today';
    }
    return `on ${formatDateToHumanDate(leaveAnytime.startDate)}`;
  }, [leaveAnytime?.startDate, now]);

  // Check if plan is eligible for cancel anytime (for next step)
  const cancelAnytime = planData?.coverage?.cancelAnytime;
  const cancelAnytimeEligible = React.useMemo(() => {
    const inRange = isWithinDateRange(
      {
        startDate: now,
        endDate: cancelAnytime?.endDate,
      },
      now
    );
    const length =
      getTerm(planData?.lease?.signDate, planData?.lease?.startDate) > 1;

    // If plan is tied to a property, check if cancel anytime is enabled
    let allowed = true;
    if (planData?.property) {
      allowed = planData?.property?.cancelAnytimeEnabled;
    }

    return inRange && length && allowed;
  }, [
    now,
    cancelAnytime,
    planData?.lease?.signDate,
    planData?.lease?.startDate,
    planData?.property,
  ]);

  // Don't render if neither limited coverage nor leave anytime is available or still loading
  if (
    isLoadingComparison ||
    (!isLimitedCoverageAvailable && !leaveAnytimeEligible)
  ) {
    return null;
  }

  // Handle error state
  if (comparisonError) {
    return (
      <div className='flex flex-col w-full h-full'>
        <ApplyTitle
          title='Choose Coverage'
          subtitle='Unable to load coverage options.'
          buttons={
            <ApplyButtons
              loading={false}
              stepNumber={stepNumber}
              isValid={false}
              handleSubmit={() => {}}
              onNextClick={onNextClick}
              onBackClick={onBackClick}
              nextTitle='Continue'
              disableBack={!onBackClick}
            />
          }
        />
        <ApplyContent>
          <Flex className='flex-col items-center justify-center p-8'>
            <Text className='text-red-500'>
              Failed to load coverage comparison data. Please try again.
            </Text>
          </Flex>
        </ApplyContent>
      </div>
    );
  }

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Choose Coverage'
        subtitle='Select your coverage type and payment plan.'
        buttons={
          <ApplyButtons
            loading={updatePlanLoading}
            stepNumber={stepNumber}
            isValid={coverageSelection[coverageTypes.LEAVE_ANYTIME]}
            handleSubmit={onNextClick}
            onNextClick={onNextClick}
            onBackClick={onBackClick}
            nextTitle={
              cancelAnytimeEligible
                ? 'Continue'
                : 'Continue to Payment'
            }
            disableBack={!onBackClick}
          />
        }
      />

      {/* main content */}
      <ApplyContent>
        {planData?.property && (
          <ApplyFormLayout
            className={`p-6 rounded-lg border relative overflow-clip ${
              brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
            }`}
            style={brand?.bgColor ? { backgroundColor: brand.bgColor } : {}}
          >
            <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
              {brand && (
                <img
                  src={isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo}
                  alt={planData?.property?.name}
                  className='max-w-[140px] max-h-[50px] object-contain'
                />
              )}
              <Flex className='flex-col gap-1'>
                <Text
                  className={`font-medium text-lg ${
                    isDarkBg ? 'text-white' : 'text-black'
                  }`}
                >
                  ReLease Coverage at {planData?.property?.name}
                </Text>
                <Text
                  className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                >
                  {planData?.property?.address?.streetAddress1},{' '}
                  {planData?.property?.address?.city},{' '}
                  {planData?.property?.address?.state}{' '}
                  {planData?.property?.address?.postalCode}
                </Text>
              </Flex>
            </Flex>
            {brand?.backgroundHeroImage && (
              <img
                src={brand?.backgroundHeroImage}
                alt={planData?.property?.name}
                className='absolute inset-0 opacity-15 z-0 object-cover'
              />
            )}
          </ApplyFormLayout>
        )}

        {/* container */}
        <Flex className='w-full flex-col gap-12'>
          {/* Leave Anytime Payment Plan Selection */}
          {leaveAnytimeEligible && (
            <Flex className='flex-col gap-6'>
              {isLimitedCoverageAvailable && (
                <CoverageTypeSelector
                  planData={planData}
                  selectedType={coverageType}
                  onTypeChange={handleCoverageTypeChange}
                  isLimitedCoverageAvailable={isLimitedCoverageAvailable}
                  limitedCoverageData={coverageComparisonData?.limited}
                  fullCoverageData={coverageComparisonData?.full}
                />
              )}

              <Flex className='flex-col w-full rounded-md border border-gray-200'>
                <Flex className='flex-col gap-1 px-6 py-4'>
                  <h2 className='text-lg font-medium'>Coverage Details</h2>
                  <Text className='text-base text-gray-500'>
                    You're covered for early move-outs with the following details:
                  </Text>
                </Flex>
                <Accordion>
                  {!isLimitedCoverageAvailable && (
                    <InfoDetail
                      title='Coverage Period'
                      subtitle={
                        <Flex className='flex-col sm:flex-row gap-2 items-start sm:items-center'>
                          <span>
                            {isMonthToMonth
                              ? `Month-to-month from ${formatDateToHumanDate(
                                  leaveAnytime?.startDate
                                )}`
                              : `${formatDateToHumanDate(
                                  leaveAnytime?.startDate
                                )} - ${formatDateToHumanDate(leaveAnytime?.endDate)}`}
                          </span>
                        </Flex>
                      }
                    >
                      {isMonthToMonth ? (
                        <Text>
                          Your coverage period starts on{' '}
                          {formatDateToHumanDate(leaveAnytime?.startDate)} and
                          renews each month until the end of your lease
                          agreement. You are eligible to leave your lease during this period and receive benefits.
                        </Text>
                      ) : (
                        <Text>
                          Your coverage period starts on{' '}
                          {formatDateToHumanDate(leaveAnytime?.startDate)} and
                          ends on {formatDateToHumanDate(leaveAnytime?.endDate)}
                          . You are eligible to leave your lease during this period and receive benefits.
                        </Text>
                      )}
                    </InfoDetail>
                  )}
                  <InfoDetail
                    title='Deductible'
                    subtitle={
                      <span>
                        {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}
                        <span className='text-gray-500'>
                          {' '}
                          (due at time of claim)
                        </span>
                      </span>
                    }
                  >
                    <Text>
                      Your coverage includes a deductible in the amount of{' '}
                      {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}.
                      This amount must be paid at the time of a claim before
                      ReLease begins covering the rest of your early termination
                      fees.
                    </Text>
                  </InfoDetail>
                  <InfoDetail
                    title='Notice Period'
                    subtitle={
                      <span>
                        {leaveAnytime?.noticePeriod || 3} days{' '}
                        <span className='text-gray-500'>
                          (before move-out date)
                        </span>
                      </span>
                    }
                  >
                    <Text>
                      You must provide at least{' '}
                      {leaveAnytime?.noticePeriod || 3} days notice before your
                      move-out date. This means you need to submit your claim at
                      least {leaveAnytime?.noticePeriod || 3} days before the
                      earliest date you want to move out.
                    </Text>
                  </InfoDetail>
                </Accordion>
              </Flex>

              <RadioGroup
                className='flex flex-col divide-y divide-gray-200 rounded-md border border-gray-200'
                value={coverageSelection[coverageTypes.LEAVE_ANYTIME]}
                onChange={(value) =>
                  dispatch(
                    saveCoverageSelection({
                      ...coverageSelection,
                      [coverageTypes.LEAVE_ANYTIME]: value,
                    })
                  )
                }
              >
                <Flex className='flex-col gap-3 px-6 py-4 sm:flex-row sm:justify-between sm:items-center'>
                  <Flex className='flex-col gap-1'>
                    <h2 className='text-lg font-medium'>Payment Plan</h2>
                    <Text className='text-base text-gray-500'>
                      Choose how you want to pay for your coverage.
                    </Text>
                  </Flex>
                  {coverageSelection[coverageTypes.LEAVE_ANYTIME] && (
                    <Flex className=''>
                      <Tag
                        colorScheme='green'
                        size='md'
                        className='px-4 py-2 flex gap-2 w-fit cursor-pointer hover:bg-green-100 transition-colors text-sm'
                        onClick={() => handleSmoothScroll('savings-calculator')}
                      >
                        <IconImage size='3xs' dark className='-mt-0.5' />
                        <span className='font-medium'>
                          You're saving {savings?.savings} with ReLease
                          <span className='hidden md:inline'>
                            {' '}
                            if you move early
                          </span>
                        </span>
                      </Tag>
                    </Flex>
                  )}
                </Flex>
                {!isMonthToMonth && (
                  <PaymentPlanCard
                    title='Pay upfront'
                    subtitle={`One payment of ${leaveAnytimeOnetimeTotal}`}
                    amount={leaveAnytimeOnetimeTotal}
                    value={planPaymentMode.ONE_TIME}
                    recurring={false}
                  />
                )}
                <PaymentPlanCard
                  title={`Pay in ${currentCoverageData?.term} monthly installments`}
                  subtitle={`Pay ${formatAmount(leaveAnytimeSubscriptionAmount * currentCoverageData?.term)} in ${currentCoverageData?.term} monthly installments of ${leaveAnytimeSubscriptionTotal}, starting ${leaveAnytimeSubscriptionStartDateText}`}
                  amount={leaveAnytimeSubscriptionTotal}
                  value={planPaymentMode.SUBSCRIPTION}
                  startDate={leaveAnytimeSubscriptionStartDateText}
                />
                <PaymentPlanCard
                  title='Customize my payment'
                  subtitle={`Split your upfront and monthly payments to what works best for you`}
                  value={planPaymentMode.SPLIT}
                  startDate={formatDateToHumanDate(leaveAnytime?.startDate)}
                  button={
                    coverageSelection[coverageTypes.LEAVE_ANYTIME] !==
                      planPaymentMode.SPLIT && (
                      <Button
                        theme='secondary'
                        size='sm'
                        title='Customize my payment'
                        onClick={() => {
                          dispatch(
                            saveCoverageSelection({
                              ...coverageSelection,
                              [coverageTypes.LEAVE_ANYTIME]:
                                planPaymentMode.SPLIT,
                            })
                          );
                        }}
                      />
                    )
                  }
                >
                  {coverageSelection[coverageTypes.LEAVE_ANYTIME] ===
                    planPaymentMode.SPLIT && (
                    <Flex className='pl-16 pr-6 sm:pt-3 pb-5 gap-3 sm:gap-2 w-full flex-col'>
                      <Flex className='w-full justify-between gap-3 items-center'>
                        {/* Upfront Payment Display */}
                        <Text className='font-medium text-xl'>
                          {formatAmount(upfrontSplit)}
                          <span className='block sm:inline text-md text-gray-500 font-normal'>
                            due today
                          </span>
                        </Text>

                        {/* Monthly Payment Display */}
                        <Text className='font-medium text-xl'>
                          {monthlySplitTotal}
                          <span className='inline text-md text-gray-500 font-normal'>
                            {' '}
                            per month{' '}
                          </span>
                          <span className='block sm:inline text-md text-gray-500 font-normal'>
                            starting {leaveAnytimeSubscriptionStartDateText}
                          </span>
                        </Text>
                      </Flex>

                      {/* Slider */}
                      <Slider
                        aria-label='upfront-payment-slider'
                        value={upfrontSplit || 0}
                        min={0}
                        max={leaveAnytimeSplitAmount}
                        step={1}
                        onChange={(val) =>
                          dispatch(saveUpfrontSplitAmount(val))
                        }
                      >
                        <SliderTrack>
                          <SliderFilledTrack className='bg-black' />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Flex>
                  )}
                </PaymentPlanCard>
              </RadioGroup>

              {coverageSelection[coverageTypes.LEAVE_ANYTIME] && (
                <SavingsCalculator
                  deductible={leaveAnytime?.deductible || DEFAULT_DEDUCTIBLE}
                  savings={savings}
                />
              )}
            </Flex>
          )}
        </Flex>
      </ApplyContent>
    </div>
  );
};

export default PlanStepCoverageType;
